import { ArrowRight } from "lucide-react";
import * as React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import {
  AlertaErro,
  Campo,
  LayoutAcesso,
  mensagemDeErro,
  problemaSenha,
  SENHA_MINIMA,
} from "@/components/acesso/LayoutAcesso";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "@/lib/api";
import { type SessaoAberta, useAuth } from "@/lib/auth";

type Modo = "convite" | "redefinicao";

const TEXTOS: Record<Modo, { titulo: string; botao: string; endpoint: string }> = {
  convite: {
    titulo: "Concluir cadastro",
    botao: "Criar senha e entrar",
    endpoint: "/auth/convite/aceitar",
  },
  redefinicao: {
    titulo: "Nova senha",
    botao: "Salvar senha e entrar",
    endpoint: "/auth/senha/redefinir",
  },
};

/**
 * Destino dos links de e-mail de convite e de redefinição: o token vem na URL,
 * a tela confere de quem é e pede a senha. Ao salvar, já entra no sistema.
 */
export function DefinirSenhaPage({ modo }: { modo: Modo }) {
  const [parametros] = useSearchParams();
  const token = parametros.get("token") ?? "";
  const { abrirSessao } = useAuth();
  const navegar = useNavigate();
  const textos = TEXTOS[modo];

  const [dono, setDono] = React.useState<{ nome: string; email: string } | null>(null);
  const [linkInvalido, setLinkInvalido] = React.useState("");
  const [senha, setSenha] = React.useState("");
  const [confirmacao, setConfirmacao] = React.useState("");
  const [erro, setErro] = React.useState("");
  const [enviando, setEnviando] = React.useState(false);

  React.useEffect(() => {
    let ativo = true;
    if (!token) {
      setLinkInvalido("O link está incompleto. Abra-o de novo a partir do e-mail.");
      return;
    }
    api<{ nome: string; email: string }>("/auth/token/verificar", {
      method: "POST",
      body: { token, tipo: modo },
    })
      .then((d) => ativo && setDono(d))
      .catch((falha) => ativo && setLinkInvalido(mensagemDeErro(falha, "Link inválido.")));
    return () => {
      ativo = false;
    };
  }, [token, modo]);

  const submeter = async (evento: React.FormEvent) => {
    evento.preventDefault();
    const problema = problemaSenha(senha, confirmacao);
    if (problema) {
      setErro(problema);
      return;
    }
    setErro("");
    setEnviando(true);
    try {
      const sessao = await api<SessaoAberta>(textos.endpoint, {
        method: "POST",
        body: { token, senha },
      });
      abrirSessao(sessao);
      navegar("/", { replace: true });
    } catch (falha) {
      setErro(mensagemDeErro(falha, "Não foi possível salvar a senha."));
      setEnviando(false);
    }
  };

  if (linkInvalido) {
    return (
      <LayoutAcesso titulo="Link indisponível" subtitulo={linkInvalido}>
        <p className="mt-6 text-sm text-muted-foreground">
          {modo === "convite"
            ? "Peça ao administrador do OLÉ para reenviar o convite."
            : "Peça um novo link na tela de entrada, em “Esqueci minha senha”."}
        </p>
        <Button className="mt-6 w-full" variant="outline" onClick={() => navegar("/", { replace: true })}>
          Ir para a tela de entrada
        </Button>
      </LayoutAcesso>
    );
  }

  if (!dono) {
    return (
      <LayoutAcesso titulo={textos.titulo} subtitulo="Conferindo o link…">
        <div className="mt-8 grid gap-4">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-12 w-full" />
        </div>
      </LayoutAcesso>
    );
  }

  return (
    <LayoutAcesso
      titulo={textos.titulo}
      subtitulo={
        modo === "convite" ? (
          <>
            Boas-vindas, <b className="text-fs-escuro">{dono.nome}</b>. Escolha a senha da conta{" "}
            <b className="text-fs-escuro">{dono.email}</b>.
          </>
        ) : (
          <>
            Escolha a nova senha da conta <b className="text-fs-escuro">{dono.email}</b>.
          </>
        )
      }
    >
      <form className="mt-8 grid gap-5" onSubmit={submeter}>
        {/* Campo oculto para o gerenciador de senhas associar a senha ao login certo. */}
        <input type="email" autoComplete="username" value={dono.email} readOnly hidden />
        <Campo
          id="senha"
          rotulo={`Senha (mínimo de ${SENHA_MINIMA} caracteres)`}
          type="password"
          autoComplete="new-password"
          required
          minLength={SENHA_MINIMA}
          autoFocus
          value={senha}
          onChange={(e) => setSenha(e.target.value)}
        />
        <Campo
          id="confirmacao"
          rotulo="Confirme a senha"
          type="password"
          autoComplete="new-password"
          required
          value={confirmacao}
          onChange={(e) => setConfirmacao(e.target.value)}
        />

        {erro && <AlertaErro>{erro}</AlertaErro>}

        <Button type="submit" size="lg" disabled={enviando} className="group">
          {enviando ? "Salvando…" : textos.botao}
          {!enviando && (
            <ArrowRight className="transition-transform duration-200 group-hover:translate-x-0.5" />
          )}
        </Button>
      </form>
    </LayoutAcesso>
  );
}
